import YAML from "yaml";

import type { Condition } from "@/types";

import { MetadataChangeset } from "../metadataChangeset";

export enum Accessibility {
  Organization = "organization",
  ActionHub = "action_hub",
}

export interface ComputeRequirements {
  vCPU: number;
  memory: number;
  storage: number;
  gpu: boolean;
}

export interface ContainerParameters {
  command: string[] | null;
  entry_point: string[] | null;
  env_vars: Record<string, string> | null;
  workdir: string | null;
}

export interface ActionReference {
  name: string;
  digest?: string;
  owner?: string;
}

export interface ActionParameter {
  name: string;
  required: boolean;
  description?: string;
  default?: unknown;
}

export interface ActionRecord {
  created: string;
  created_by: string;
  modified: string;
  modified_by: string;
  name: string;
  org_id: string;
  accessibility: Accessibility;
  compute_requirements?: ComputeRequirements;
  container_parameters: ContainerParameters;
  description?: string;
  digest?: string;
  inherits?: ActionReference;
  metadata: Record<string, string>;
  parameters?: ActionParameter[];
  published?: string;
  tags: string[];
  uri?: string;
  short_description?: string;
  timeout?: number;
  requires_downloaded_inputs: boolean | null; // defaults to true if unset
}

export function isActionRecord(obj: unknown): obj is ActionRecord {
  return typeof obj === "object" && obj !== null && "name" in obj;
}

export interface CreateActionRequest {
  name: string;
  compute_requirements?: ComputeRequirements | null;
  container_parameters?: ContainerParameters | null;
  description?: string;
  metadata?: Record<string, unknown>;
  parameters?: ActionParameter[];
  tags?: string[];
  // The action-timeout form field holds its raw text-input value, so a
  // numeric string may be sent; the backend coerces it.
  timeout?: number | string | null;
  uri?: string;
}

export interface ParameterChangeset {
  put_parameters?: ActionParameter[];
  remove_parameters?: string[];
}

export type UpdateActionRequest = Partial<{
  compute_requirements: ComputeRequirements;
  container_parameters: ContainerParameters;
  description: string;
  inherits: ActionReference;
  metadata_changeset: MetadataChangeset;
  parameter_changeset: ParameterChangeset;
  uri: string | null;
  short_description: string;
  timeout: number;
  requires_downloaded_inputs: boolean;
}>;

/**
 * Response from cancelling active invocations.
 */
export interface CancelActiveInvocationsResponse {
  /** Number of invocations successfully cancelled */
  success_count: number;
  /** Number of invocations that failed to be cancelled */
  failure_count: number;
  /** Whether there are more invocations to cancel (for pagination) */
  has_more: boolean;
}

/**
 * How deep an org's invocation queue is, and how long it is likely to take to drain.
 */
export interface InvocationQueueStats {
  /**
   * Live count per invocation status name, non-terminal statuses only. A status with nothing in
   * it is omitted rather than reported as zero, so read it with {@link queuedCount} and
   * {@link runningCount} instead of indexing directly.
   */
  count_by_status: Record<string, number>;
  org_id: string;
  /** The org's cap on simultaneously running invocations, or null when it has no cap. */
  concurrency_limit: number | null;
  /**
   * Median runtime of invocations created in the last 7 days that ran to completion, or null when
   * none did. The window is on creation, not completion, so a long job that started before the
   * window is not sampled. Failed and cancelled invocations are excluded.
   */
  median_duration_seconds: number | null;
  /**
   * Expected runtime of everything queued, added up: each queued invocation at its own action's
   * 7-day median, or the org's when its action has none. Null when nothing is queued or no median
   * exists. Older servers omit it.
   */
  queued_work_seconds?: number | null;
}

export type ActivityPeriod =
  | "1m"
  | "5m"
  | "15m"
  | "1h"
  | "6h"
  | "1d"
  | "1w"
  | "1mo";

/** Body of `POST /v1/actions/invocations/activity`. */
export interface InvocationActivityRequest {
  /** Inclusive, ISO 8601. The server widens it back to the bucket boundary it falls in. */
  start_time: string;
  /** Exclusive, ISO 8601. */
  end_time: string;
  /** Omit for the finest bucket the window allows; asking for a finer one is a 400. */
  period?: ActivityPeriod;
  /** Same vocabulary as `POST /v1/actions/invocations/query`. */
  condition?: Condition;
}

/** What the matching invocations were doing during one bucket. */
export interface InvocationActivityBucket {
  period_start: string;
  /**
   * Most invocations in each non-terminal status at any one instant, keyed by status *name*;
   * statuses empty throughout are absent. Peaks do not add across statuses.
   */
  peak_in_status: Record<string, number>;
  /** Most invocations holding a compute slot (Scheduled through Uploading) at any one instant. */
  peak_concurrency: number;
  /** Transitions into each status, keyed by status name; zeros absent. Queued means submissions. */
  entered_status: Record<string, number>;
  /** Spread over each invocation's runtime, so a long job burns evenly across the buckets it spans. */
  compute_credit_millionths: number;
}

export interface InvocationActivityResponse {
  period: ActivityPeriod;
  /** Start of the first bucket. */
  start_time: string;
  /** The requested end, exclusive. */
  end_time: string;
  /** Oldest first; buckets in which nothing happened are absent. */
  buckets: InvocationActivityBucket[];
}

export type InvocationGroupBy = "action" | "source" | "submitter";

/** Body of `POST /v1/actions/invocations/breakdown`. */
export interface InvocationBreakdownRequest {
  /** Count invocations whose *current* status is one of these. */
  statuses: InvocationStatus[];
  /** A group is one distinct combination of these dimensions' values. */
  group_by: InvocationGroupBy[];
  condition?: Condition;
  /** 1–100, default 20. */
  limit?: number;
}

/** One group of a breakdown; the fields of dimensions not grouped on are null. */
export interface InvocationBreakdownGroup {
  action_org_id: string | null;
  action_name: string | null;
  /** `"Trigger"` or `"Manual"`; set when grouped by source or submitter. */
  source_type: string | null;
  /** Trigger id or user id per `source_type`; set when grouped by submitter. */
  source_id: string | null;
  /** The trigger's current name when `source_id` is a trigger that still exists. */
  trigger_name: string | null;
  count: number;
}

export interface InvocationBreakdownResponse {
  /** Largest first, and at most `limit` of them. */
  groups: InvocationBreakdownGroup[];
  /**
   * How many invocations match the requested statuses and condition, whatever `limit` and the
   * grouping are. `groups` is truncated, so summing its counts undercounts exactly when there is
   * most to count; take the total from here.
   */
  total_count: number;
}

/** `count_by_status` is keyed by status *name*, so go through the enum's reverse mapping. */
function countForStatus(
  stats: InvocationQueueStats,
  status: InvocationStatus,
): number {
  return stats.count_by_status[InvocationStatus[status]] ?? 0;
}

/** Invocations waiting for capacity — not yet handed to compute. */
export function queuedCount(stats: InvocationQueueStats): number {
  return countForStatus(stats, InvocationStatus.Queued);
}

/** Invocations that hold a compute slot, whether or not they have started executing. */
export function runningCount(stats: InvocationQueueStats): number {
  return [
    InvocationStatus.Scheduled,
    InvocationStatus.Downloading,
    InvocationStatus.Processing,
    InvocationStatus.Uploading,
  ].reduce((total, status) => total + countForStatus(stats, status), 0);
}

export enum InvocationDataSourceType {
  Dataset = "Dataset",
  Unknown = "Unknown",
}

export interface InvocationDataSource {
  data_source_type: InvocationDataSourceType;
  data_source_id: string;
}

export const UNSPECIFIED_DATA_SOURCE: InvocationDataSource = {
  data_source_id: "__UNSPECIFIED_DATA_SOURCE__",
  data_source_type: InvocationDataSourceType.Dataset,
};

export function isDataSourceSpecified(
  dataSource: InvocationDataSource,
): boolean {
  // Note: this must match the SDK constant defined in invocation_record.py
  return dataSource.data_source_id !== UNSPECIFIED_DATA_SOURCE.data_source_id;
}

export enum UploadDestinationType {
  Dataset = "Dataset",
}

export interface InvocationUploadDestination {
  destination_type: UploadDestinationType;
  destination_id: string | null;
}

export enum InvocationStatus {
  Queued = 0,
  Scheduled = 1,
  Downloading = 2,
  Processing = 3,
  Uploading = 4,
  Completed = 5,
  Cancelled = 997,
  Failed = 998,
  Deadly = 999,
}

export const invocationStatusToString = (status?: InvocationStatus) => {
  switch (status) {
    case InvocationStatus.Queued:
      return "Pending";
    case InvocationStatus.Scheduled:
      return "Provisioning";
    case InvocationStatus.Downloading:
    case InvocationStatus.Processing:
    case InvocationStatus.Uploading: // Fall through
      return "Running";
    case InvocationStatus.Completed:
      return "Completed";
    case InvocationStatus.Cancelled:
      return "Cancelled";
    case InvocationStatus.Failed:
    case InvocationStatus.Deadly: // Fall through
      return "Failed";
  }
  return "Unknown";
};

export interface ActionProvenance {
  name: string;
  org_id: string;
  digest?: string;
}

export interface ExecutableProvenance {
  container_image_uri?: string;
  container_image_digest?: string;
}

export enum InvocationSource {
  Trigger = "Trigger",
  Manual = "Manual",
}

export interface SourceProvenance {
  source_type: InvocationSource;
  source_id: string;
}

export interface InvocationProvenance {
  action: ActionProvenance;
  executable: ExecutableProvenance;
  source: SourceProvenance;
}

export interface InvocationStatusRecord {
  status: InvocationStatus;
  detail?: string;
  timestamp: string;
}

export interface DataSelector {
  query: string | null;
  ids: string[] | null;
  names: string[] | null;
  dataset_id: string | null;
}

export interface FileSelector extends DataSelector {
  paths: string[] | null;
}

export interface InvocationInput {
  files: FileSelector | FileSelector[] | null;
  topics: DataSelector | DataSelector[] | null;
}

const EMPTY_INVOCATION_INPUT: InvocationInput = {
  files: null,
  topics: null,
};

const EMPTY_DATA_SELECTOR: DataSelector = {
  query: null,
  names: null,
  ids: null,
  dataset_id: null,
};

const EMPTY_FILE_SELECTOR: FileSelector = {
  ...EMPTY_DATA_SELECTOR,
  paths: null,
};

export class InvocationInputs {
  static fileQuery(query: string): InvocationInput {
    return {
      files: {
        ...EMPTY_FILE_SELECTOR,
        query: query,
      },
      topics: null,
    };
  }

  static topicQuery(query: string): InvocationInput {
    return {
      topics: {
        ...EMPTY_DATA_SELECTOR,
        query: query,
      },
      files: null,
    };
  }
}

export interface InvocationRecord {
  created: string;
  data_source: InvocationDataSource;
  input_data: string[];
  rich_input_data: InvocationInput | null;
  invocation_id: string;
  idempotency_id?: string;
  compute_requirements: ComputeRequirements;
  container_parameters: ContainerParameters;
  last_status: InvocationStatus;
  org_id: string;
  parameter_values: Record<string, string>;
  provenance: InvocationProvenance;
  status: InvocationStatusRecord[];
  duration: string;
  timeout: number | null;
  upload_destination: InvocationUploadDestination | null;
}

export function isInvocationRecord(obj: unknown): obj is InvocationRecord {
  return typeof obj === "object" && obj !== null && "invocation_id" in obj;
}

export function getInvocationInput(
  invocation: InvocationRecord,
  includeDatasetId: boolean = false,
): InvocationInput {
  if (invocation.rich_input_data) {
    return invocation.rich_input_data;
  }

  if (isDataSourceSpecified(invocation.data_source)) {
    return {
      files: {
        ...EMPTY_FILE_SELECTOR,
        paths: invocation.input_data,
        dataset_id: includeDatasetId
          ? invocation.data_source.data_source_id
          : null,
      },
      topics: null,
    };
  }

  return EMPTY_INVOCATION_INPUT;
}

export function invocationInputToYaml(
  invocationInput: InvocationInput,
): string {
  if (invocationInput === EMPTY_INVOCATION_INPUT) {
    return "None";
  }

  return YAML.stringify(invocationInput, (_key, value) => {
    // Filter out null fields from the input spec.
    if (value === null) {
      return undefined;
    }

    return value;
  });
}

export enum InvocationProcess {
  Setup = "setup",
  Action = "action",
  OutputHandler = "output_handler",
}

export interface InvocationLogRecord {
  partial_id?: string;
  log: string;
  process: InvocationProcess;
  timestamp: string;
}
